import React, { useContext, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { LayoutDashboard, Users, FileText, Calendar, Phone, DollarSign, BarChart3, UserPlus, LogOut, Menu, X, Settings, TrendingUp, Handshake, Wallet, Clipboard, Tag, ListTodo, ClipboardList, UserCog } from 'lucide-react';

const Layout = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { name: 'Ajanda', path: '/agenda', icon: ListTodo },
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Günlük Aktivite', path: '/daily-activities', icon: ClipboardList },
    { name: 'Günlük Görüşmeler', path: '/call-logs', icon: Clipboard },
    { name: 'Fırsatlar', path: '/opportunities', icon: Tag, adminOnly: true },
    { name: 'Müşteri Adayları', path: '/leads', icon: UserPlus },
    { name: 'Demo Müşterileri', path: '/demo-customers', icon: Calendar, adminOnly: true },
    { name: 'Müşteriler', path: '/customers', icon: Users },
    { name: 'Teklifler', path: '/quotes', icon: FileText },
    { name: 'Ziyaretler', path: '/visits', icon: Calendar },
    { name: 'Aramalar', path: '/calls', icon: Phone },
    { name: 'Satışlar', path: '/sales', icon: DollarSign },
    { name: 'Primler', path: '/employee-commissions', icon: Wallet, companyOnly: true },
    { name: 'İş Ortağı Hakediş', path: '/dealer-sales', icon: TrendingUp },
    { name: 'Raporlar', path: '/reports', icon: BarChart3 },
    { name: 'Abonelik Yenilemeleri', path: '/subscription-renewals', icon: TrendingUp, adminCompanyOnly: true },
  ];

  // Dealer menu items (only these will show for dealers) - İş ortakları Günlük Aktivite görmez
  const dealerMenuItems = [
    { name: 'Ajanda', path: '/agenda', icon: ListTodo },
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Günlük Görüşmeler', path: '/call-logs', icon: Clipboard },
    { name: 'Müşteri Adayları', path: '/leads', icon: UserPlus },
    { name: 'Müşterilerim', path: '/customers', icon: Users },
    { name: 'Teklifler', path: '/quotes', icon: FileText },
    { name: 'Aramalar', path: '/calls', icon: Phone },
    { name: 'Satışlar', path: '/dealer-sales', icon: DollarSign },
    { name: 'Müşteri Yöneticileri', path: '/my-managers', icon: UserCog, ownerOnly: true },
    { name: 'Hakediş', path: '/my-commissions', icon: TrendingUp, ownerOnly: true },
  ];

  // Admin-only menu items (Yönetim Modülü)
  const adminMenuItems = [
    { name: 'İş Ortakları', path: '/partners', icon: Handshake },
    { name: 'Personel Primleri', path: '/admin/employee-commissions', icon: Wallet },
    { name: 'Kullanıcılar', path: '/users', icon: Settings },
    { name: 'Personeller', path: '/personnel', icon: Users },
    { name: 'Gelirler - Giderler', path: '/finances', icon: DollarSign },
  ];

  const navItemClass = (isActive) =>
    `sidebar-link group flex items-center space-x-3 px-4 py-2.5 rounded-xl text-sm font-medium ${
      isActive
        ? 'bg-black text-white font-semibold shadow-sm'
        : 'text-[#8A95A1] hover:bg-[#F5F7F9] hover:text-black'
    }`;

  const renderNavLink = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path ||
                     (item.path !== '/' && location.pathname.startsWith(item.path));
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setMobileMenuOpen(false)}
        data-testid={`nav-${item.name.toLowerCase()}`}
        className={navItemClass(isActive)}
      >
        <Icon size={18} strokeWidth={2} className={isActive ? 'text-white' : 'text-[#9AA6B2] group-hover:text-black'} />
        <span>{item.name}</span>
      </Link>
    );
  };

  return (
    <div className="flex h-screen bg-[#F5F7F9] font-manrope">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-toggle"
        className="lg:hidden fixed top-4 left-4 z-50 bg-white p-2.5 rounded-xl border border-[#EBEEF2] premium-shadow text-black"
      >
        {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Premium minimal */}
      <aside className={`w-64 bg-white border-r border-[#EBEEF2] flex flex-col fixed lg:static h-full z-40 transform transition-transform duration-300 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`}>
        <div className="px-5 py-6 border-b border-[#EEF1F4]">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-black rounded-xl flex items-center justify-center">
              <span className="text-white font-extrabold text-sm tracking-tighter">P</span>
            </div>
            <div>
              <h1 className="text-base font-extrabold tracking-tighter text-black leading-none">PROGOFILOAI</h1>
              <p className="text-[10px] font-medium text-[#8A95A1] mt-1 tracking-wide">Fleet Management Software</p>
            </div>
          </div>
          {user?.role === 'dealer_owner' && user?.company_name && (
            <div className="mt-5 pt-5 border-t border-[#EEF1F4]">
              <div className="bg-[#F5F7F9] border border-[#EBEEF2] p-3 rounded-xl">
                <p className="section-label">Şirket</p>
                <p className="text-sm font-bold text-black mt-1.5">{user.company_name}</p>
              </div>
            </div>
          )}
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto hide-scrollbar">
          {(user?.role === 'dealer_owner' || user?.role === 'dealer_sales_rep') ? (
            <>
              <p className="px-4 pb-2 section-label">
                {user?.role === 'dealer_owner' ? 'İş Ortağı Menü' : 'Bayi Satış Temsilcisi Menü'}
              </p>
              {dealerMenuItems.filter((item) => !item.ownerOnly || user?.role === 'dealer_owner').map(renderNavLink)}
            </>
          ) : (
            <>
              {menuItems.map((item) => {
                if (item.adminOnly && user?.role !== 'admin' && user?.role !== 'company_sales_manager') {
                  return null;
                }
                if (item.adminCompanyOnly && user?.role !== 'admin' && user?.role !== 'company_sales_manager') {
                  return null;
                }
                if (item.companyOnly && user?.role !== 'company_sales_manager') {
                  return null;
                }
                return renderNavLink(item);
              })}
            </>
          )}

          {/* Admin Only Section */}
          {user?.role === 'admin' && (
            <>
              <div className="border-t border-[#EEF1F4] my-3 pt-3">
                <p className="px-4 pb-2 section-label">Yönetim</p>
              </div>
              {adminMenuItems.map(renderNavLink)}
            </>
          )}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white/80 backdrop-blur-xl border-b border-[#EEF1F4] px-4 sm:px-6 py-3 sticky top-0 z-20">
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-semibold text-black leading-tight" data-testid="user-name">{user?.full_name}</p>
                <p className="text-[11px] text-[#8A95A1] font-medium capitalize" data-testid="user-role">{user?.role}</p>
              </div>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="flex items-center space-x-1.5 px-4 py-2 rounded-full border border-[#EBEEF2] text-[#8A95A1] hover:text-black hover:bg-[#F5F7F9] transition-colors text-sm font-medium active:scale-[0.98]"
              >
                <LogOut size={15} />
                <span>Çıkış</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-auto pb-20 lg:pb-0 bg-[#F5F7F9]">
          <div className="p-4 sm:p-6 lg:p-8 max-w-full">
            <Outlet />
          </div>
        </div>
      </main>

      {/* Bottom Navigation - Mobile Only */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#EBEEF2] z-40 premium-shadow">
        <div className="grid grid-cols-5 gap-1">
          {[
            { name: 'Dashboard', path: '/', icon: LayoutDashboard },
            { name: 'Müşteriler', path: '/customers', icon: Users },
            { name: 'Satışlar', path: '/sales', icon: DollarSign },
            { name: 'Aramalar', path: '/calls', icon: Phone },
            { name: 'Menü', path: null, icon: Menu, isMenuTrigger: true }
          ].map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            if (item.isMenuTrigger) {
              return (
                <button
                  key="menu-trigger"
                  onClick={() => setMobileMenuOpen(true)}
                  data-testid="mobile-nav-menu"
                  className="flex flex-col items-center justify-center py-2.5 text-[#8A95A1] hover:text-black transition-colors"
                >
                  <Icon size={20} />
                  <span className="text-[10px] mt-1 font-medium">Menü</span>
                </button>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                className={`flex flex-col items-center justify-center py-2.5 transition-colors ${
                  isActive ? 'text-black' : 'text-[#8A95A1] hover:text-black'
                }`}
              >
                <Icon size={20} />
                <span className="text-[10px] mt-1 font-medium">{item.name}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
